import { lumi } from '@/lib/lumi'

export type PostCommentKind = 'wallet' | 'project'

export type PostComment = {
  id: string
  postKind: PostCommentKind
  postId: string
  wallet: string
  content: string
  createdAt: string
}

const AUTH_PREFIX = 'Penguin Pad: authorize post comment'
const ADMIN_AUTH_PREFIX = 'Penguin Pad: authorize admin delete'
const AUTH_TTL_MS = 30 * 60 * 1000
const COMMENT_CREATED_EVENT = 'penguin-pad-post-comment-created'
const COMMENT_DELETED_EVENT = 'penguin-pad-post-comment-deleted'

function buildAuthMessage(wallet: string, prefix: string) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${prefix}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function invokeErrorMessage(error: unknown, fallback: string) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || fallback).slice(0, 180)
}

function parseComments(value: unknown): PostComment[] {
  if (!Array.isArray(value))
    return []
  const rows: PostComment[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object')
      continue
    const row = item as Record<string, unknown>
    const id = String(row.id ?? row._id ?? '')
    const postKind = row.postKind === 'project' ? 'project' : row.postKind === 'wallet' ? 'wallet' : ''
    const postId = typeof row.postId === 'string' ? row.postId : ''
    const wallet = typeof row.wallet === 'string' ? row.wallet : ''
    const content = typeof row.content === 'string' ? row.content : ''
    const createdAt = typeof row.createdAt === 'string' ? row.createdAt : ''
    if (!id || !postKind || !postId || !wallet || !content)
      continue
    rows.push({ id, postKind, postId, wallet, content, createdAt })
  }
  return rows
}

export async function listPostComments(postKind: PostCommentKind, postId: string, limit = 50): Promise<PostComment[]> {
  const key = postId?.trim()
  if (!key)
    return []
  try {
    const result = await lumi.functions.invoke('postComments', {
      method: 'POST',
      body: { action: 'list', postKind, postId: key, limit },
    }) as { ok?: boolean, comments?: unknown } | undefined
    if (!result || result.ok === false)
      return []
    return parseComments(result.comments)
  }
  catch (error) {
    console.error('List post comments failed:', error)
    return []
  }
}

export async function createPostComment(input: {
  postKind: PostCommentKind
  postId: string
  wallet: string
  content: string
  signMessage: (message: string) => Promise<string>
}): Promise<PostComment> {
  const wallet = input.wallet?.trim().toLowerCase() || ''
  const content = input.content.trim()
  const postId = input.postId.trim()
  if (!wallet)
    throw new Error('Connect your wallet to comment')
  if (!content)
    throw new Error('Write a comment before posting')
  if (!postId)
    throw new Error('Missing post reference')

  const message = buildAuthMessage(wallet, AUTH_PREFIX)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('postComments', {
      method: 'POST',
      body: { action: 'create', postKind: input.postKind, postId, wallet, message, signature, content },
    }) as { ok?: boolean, error?: string, comment?: unknown } | undefined
    if (!result || result.ok === false || !result.comment)
      throw new Error(result?.error || 'Could not publish this comment')
    const [comment] = parseComments([result.comment])
    if (!comment)
      throw new Error('Could not publish this comment')
    if (typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent<PostComment>(COMMENT_CREATED_EVENT, { detail: comment }))
    return comment
  }
  catch (error) {
    console.error('Create post comment failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not publish this comment'))
  }
}

export async function deletePostComment(input: {
  commentId: string
  wallet: string
  signMessage: (message: string) => Promise<string>
}): Promise<void> {
  const wallet = input.wallet?.trim().toLowerCase() || ''
  if (!wallet)
    throw new Error('Connect the admin wallet to delete')
  const message = buildAuthMessage(wallet, ADMIN_AUTH_PREFIX)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')
  try {
    const result = await lumi.functions.invoke('postComments', {
      method: 'POST',
      body: { action: 'delete', commentId: input.commentId, wallet, message, signature },
    }) as { ok?: boolean, error?: string } | undefined
    if (!result || result.ok === false)
      throw new Error(result?.error || 'Could not delete this comment')
    if (typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent(COMMENT_DELETED_EVENT, { detail: { commentId: input.commentId } }))
  }
  catch (error) {
    console.error('Delete post comment failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not delete this comment'))
  }
}

export function onPostCommentCreated(listener: (comment: PostComment) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<PostComment>).detail
    if (!detail?.postId)
      return
    listener(detail)
  }
  window.addEventListener(COMMENT_CREATED_EVENT, handler)
  return () => window.removeEventListener(COMMENT_CREATED_EVENT, handler)
}

export function onPostCommentDeleted(listener: (commentId: string) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ commentId?: string }>).detail
    if (!detail?.commentId)
      return
    listener(detail.commentId)
  }
  window.addEventListener(COMMENT_DELETED_EVENT, handler)
  return () => window.removeEventListener(COMMENT_DELETED_EVENT, handler)
}
